/**
 * First-touch attribution capture. Spec of record:
 * ~/choice-graphics/marketing/conversion-events/site-tracking-contract.md
 * section 4.
 *
 * The WordPress plugin the app replaces stored the arriving click id and UTMs
 * and put them on every form. These helpers do the same from four sources, in
 * this order: the landing URL's query string, the GTM Conversion Linker
 * cookies, the click collector's `_cuft_click`, and the number-lease cookie
 * `cw_ct`. The result is written once to the first-party cookie `cg_attr` and
 * read back on the server when a lead is persisted and delivered to n8n.
 *
 * Everything here is pure. The browser side is a thin effect
 * (src/components/analytics/attribution-capture.tsx); the server side reads
 * the cookie in src/lib/services/leads/webhook.ts.
 */

export const ATTRIBUTION_COOKIE = 'cg_attr'
export const ATTRIBUTION_MAX_AGE_DAYS = 90
/** Browsers drop a cookie whose whole name=value pair passes ~4 KB. */
export const ATTRIBUTION_MAX_VALUE_BYTES = 3800
/** Longest value kept for any single field; bounds a hostile query string. */
export const ATTRIBUTION_MAX_FIELD_LENGTH = 512

/** Click ids that mean the visit was paid for, and so may overwrite a first touch. */
export const PAID_CLICK_KEYS = ['gclid', 'gbraid', 'wbraid', 'msclkid', 'fbclid', 'ttclid'] as const

/** Query parameters copied verbatim, including the ValueTrack fields the Ads suffix adds. */
export const URL_ATTRIBUTION_KEYS = [
  ...PAID_CLICK_KEYS,
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'campaignid',
  'assetgroupid',
  'adgroupid',
  'keyword',
  'matchtype',
  'device',
  'network',
  'targetid',
  'loc_physical',
] as const

export const ATTRIBUTION_KEYS = [
  'landing_url',
  'referrer',
  'captured_at',
  ...URL_ATTRIBUTION_KEYS,
  'lease_id',
  'pool_number',
] as const

export type AttributionKey = (typeof ATTRIBUTION_KEYS)[number]
export type Attribution = Partial<Record<AttributionKey, string>>

/**
 * Dropped first when the cookie would not fit, least useful last to keep.
 * Click ids, UTMs, the landing URL and the lease stay to the end: they are
 * what Thor and the lead sheet are keyed on.
 */
const SHRINK_ORDER: AttributionKey[] = [
  'keyword',
  'targetid',
  'loc_physical',
  'network',
  'device',
  'matchtype',
  'adgroupid',
  'assetgroupid',
  'campaignid',
  'referrer',
]

/** The swap tag's own id shape, copied from gtm/dni-tag.html so the two agree. */
const ID_RE = /^[A-Za-z0-9_-]{10,200}$/
const GCL_RE = /^GCL\.\d+\.(.+)$/
const GCL_AG_RE = /\.k([A-Za-z0-9_-]+)\$/
const UET_RE = /^_uet([0-9a-f]{32})$/i
const HEX32_RE = /^[0-9a-f]{32}$/i

function clean(value: string | null | undefined): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  if (trimmed === '') return undefined
  return trimmed.slice(0, ATTRIBUTION_MAX_FIELD_LENGTH)
}

/** Parses a `document.cookie` or `Cookie:` header string into a plain map. */
export function parseCookieString(raw: string | null | undefined): Record<string, string> {
  const out: Record<string, string> = {}
  if (!raw) return out
  for (const part of raw.split(';')) {
    const eq = part.indexOf('=')
    if (eq < 1) continue
    const name = part.slice(0, eq).trim()
    if (!name || name in out) continue
    const value = part.slice(eq + 1).trim()
    if (value === '') continue
    try {
      out[name] = decodeURIComponent(value)
    } catch {
      out[name] = value
    }
  }
  return out
}

/** Section 4.1: the click ids, UTMs and ValueTrack fields on the landing URL. */
export function parseUrlAttribution(url: string): Attribution {
  let params: URLSearchParams
  try {
    params = new URL(url, 'https://www.choice.graphics').searchParams
  } catch {
    return {}
  }
  const out: Attribution = {}
  for (const key of URL_ATTRIBUTION_KEYS) {
    const value = clean(params.get(key))
    if (value) out[key] = value
  }
  return out
}

/** Section 4.2: the Conversion Linker cookies, which outlive the query string. */
export function parseLinkerCookies(cookies: Record<string, string>): Attribution {
  const out: Attribution = {}
  const gclid = GCL_RE.exec(cookies._gcl_aw ?? '')?.[1]
  if (gclid && ID_RE.test(gclid)) out.gclid = gclid
  const wbraid = GCL_RE.exec(cookies._gcl_gb ?? '')?.[1]
  if (wbraid && ID_RE.test(wbraid)) out.wbraid = wbraid
  const gbraid = GCL_AG_RE.exec(cookies._gcl_ag ?? '')?.[1]
  if (gbraid && ID_RE.test(gbraid)) out.gbraid = gbraid
  const msclkid = UET_RE.exec(cookies._uetmsclkid ?? '')?.[1]
  if (msclkid) out.msclkid = msclkid.toLowerCase()
  return out
}

/**
 * Section 4.3: the click collector's cookie. A 32-hex value is a Microsoft
 * click id; anything else 20 characters or longer in the id alphabet is a
 * Google one.
 */
export function parseCuftClick(value: string | null | undefined): Attribution {
  const v = clean(value)
  if (!v) return {}
  if (HEX32_RE.test(v)) return { msclkid: v.toLowerCase() }
  if (v.length >= 20 && ID_RE.test(v)) return { gclid: v }
  return {}
}

/**
 * Section 4.4: the number-lease cookie, JSON `{ v, l, n, e }`. Carrying the
 * lease with the form ties it to the pool number the visitor was shown.
 */
export function parseCallTrackingCookie(value: string | null | undefined): Attribution {
  const v = clean(value)
  if (!v) return {}
  let parsed: unknown
  try {
    parsed = JSON.parse(v)
  } catch {
    return {}
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
  const record = parsed as Record<string, unknown>
  const out: Attribution = {}
  const leaseId = clean(typeof record.l === 'string' ? record.l : undefined)
  const poolNumber = clean(typeof record.n === 'string' ? record.n : undefined)
  if (leaseId) out.lease_id = leaseId
  if (poolNumber) out.pool_number = poolNumber
  return out
}

export function hasPaidClickId(attribution: Attribution): boolean {
  return PAID_CLICK_KEYS.some((key) => Boolean(attribution[key]))
}

export interface AttributionSources {
  /** Full URL of the page being rendered, query intact. */
  url: string
  referrer?: string | null
  /** The document's cookies, already parsed. */
  cookies?: Record<string, string>
  /** ISO timestamp; injected so tests are deterministic. */
  capturedAt: string
}

/**
 * The candidate first touch: URL parameters win, then the linker cookies,
 * then `_cuft_click`, and the lease is added whatever the source.
 */
export function buildAttribution({ url, referrer, cookies = {}, capturedAt }: AttributionSources): Attribution {
  const fromUrl = parseUrlAttribution(url)
  const fromLinker = parseLinkerCookies(cookies)
  const fromCollector = parseCuftClick(cookies._cuft_click)
  const fromLease = parseCallTrackingCookie(cookies.cw_ct)

  const out: Attribution = { ...fromLease, ...fromCollector, ...fromLinker, ...fromUrl }
  const landingUrl = clean(url)
  if (landingUrl) out.landing_url = landingUrl
  const ref = clean(referrer)
  if (ref) out.referrer = ref
  out.captured_at = capturedAt
  return out
}

/**
 * First touch wins. An existing cookie is replaced only when the current URL
 * carries a paid click id, which is a new paid visit and the one thing worth
 * re-attributing.
 */
export function nextAttribution(
  existing: Attribution | null,
  candidate: Attribution,
  opts: { urlHasPaidClickId: boolean }
): Attribution | null {
  if (!existing) return candidate
  if (opts.urlHasPaidClickId) return candidate
  return null
}

/** Reads the cookie back, tolerating the percent-encoded and the raw form. */
export function parseAttributionCookie(value: string | null | undefined): Attribution | null {
  if (!value) return null
  let text = value.trim()
  if (!text.startsWith('{')) {
    try {
      text = decodeURIComponent(text)
    } catch {
      return null
    }
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
  const record = parsed as Record<string, unknown>
  const out: Attribution = {}
  for (const key of ATTRIBUTION_KEYS) {
    const v = record[key]
    if (typeof v !== 'string') continue
    const cleaned = clean(v)
    if (cleaned) out[key] = cleaned
  }
  return out
}

/**
 * The cookie value: percent-encoded JSON with empty keys omitted, shrunk by
 * dropping the least useful fields until it fits.
 */
export function serializeAttribution(
  attribution: Attribution,
  maxBytes: number = ATTRIBUTION_MAX_VALUE_BYTES
): string {
  const kept: Attribution = {}
  for (const key of ATTRIBUTION_KEYS) {
    const value = clean(attribution[key])
    if (value) kept[key] = value
  }
  let encoded = encodeURIComponent(JSON.stringify(kept))
  for (const key of SHRINK_ORDER) {
    if (encoded.length <= maxBytes) break
    if (!(key in kept)) continue
    delete kept[key]
    encoded = encodeURIComponent(JSON.stringify(kept))
  }
  if (encoded.length > maxBytes && kept.landing_url) {
    // Last resort: keep the path, drop the query the click ids already carry.
    kept.landing_url = kept.landing_url.split('?')[0]
    encoded = encodeURIComponent(JSON.stringify(kept))
  }
  return encoded
}

/**
 * The cookie domain: `.choice.graphics` on the real site so the app, the
 * collector and the tagging server all see the same jar; host-only anywhere
 * else (localhost, preview hosts, an IP), where a Domain attribute is either
 * rejected or wrong.
 */
export function attributionCookieDomain(hostname: string | null | undefined): string | null {
  const host = (hostname ?? '').trim().toLowerCase().replace(/\.$/, '')
  if (host === 'choice.graphics' || host.endsWith('.choice.graphics')) return '.choice.graphics'
  return null
}

/** The full `document.cookie` assignment for a captured attribution object. */
export function attributionCookieString(
  attribution: Attribution,
  opts: { hostname?: string | null; secure?: boolean; maxAgeDays?: number } = {}
): string {
  const maxAge = (opts.maxAgeDays ?? ATTRIBUTION_MAX_AGE_DAYS) * 24 * 60 * 60
  const parts = [
    `${ATTRIBUTION_COOKIE}=${serializeAttribution(attribution)}`,
    'Path=/',
    `Max-Age=${maxAge}`,
    'SameSite=Lax',
  ]
  const domain = attributionCookieDomain(opts.hostname)
  if (domain) parts.push(`Domain=${domain}`)
  if (opts.secure) parts.push('Secure')
  return parts.join('; ')
}
